const SMS_CAMPAIGNS_STORAGE_KEY = "b-platform.marketing.sms.campaigns.v1";
const SMS_AUTOMATIONS_STORAGE_KEY = "b-platform.marketing.sms.automations.v1";

export const SMS_EDITOR_CHARACTER_LIMIT = 160;

export const SMS_AUTOMATION_STATUS_OPTIONS = [
  { value: "active", label: "Active", color: "green" },
  { value: "paused", label: "Paused", color: "amber" },
  { value: "draft", label: "Draft", color: "gray" },
];

export const SMS_AUTOMATION_TRIGGER_OPTIONS = [
  { value: "welcome", label: "Welcome automation" },
];

export const SMS_AUTOMATION_REPEAT_UNIT_OPTIONS = [
  { value: "hours", label: "hours" },
  { value: "days", label: "days" },
  { value: "weeks", label: "weeks" },
  { value: "months", label: "months" },
];

function createTimestamp(hoursAgo) {
  return new Date(Date.now() - hoursAgo * 60 * 60 * 1000).toISOString();
}

function buildDefaultSmsCampaigns() {
  return [
    {
      id: "sms-campaign-loyalty-drop",
      name: "Loyalty lunch drop",
      templateId: "sms-template-promo",
      templateName: "Friday VIP drop",
      audienceType: "segment",
      segmentIds: ["12", "24"],
      audienceLabel: "VIP regulars, Lunch guests",
      recipientCount: 1284,
      deliveredCount: 1272,
      clickRate: 18.4,
      optOutRate: 0.5,
      sendMode: "scheduled",
      scheduledSendAt: createTimestamp(-18),
      status: "scheduled",
      updatedAt: createTimestamp(2),
    },
    {
      id: "sms-campaign-review-request",
      name: "Weekend review follow-up",
      templateId: "sms-template-review",
      templateName: "48-hour review nudge",
      audienceType: "all_contacts",
      segmentIds: [],
      audienceLabel: "All SMS-consented guests",
      recipientCount: 846,
      deliveredCount: 825,
      clickRate: 11.2,
      optOutRate: 0.3,
      sendMode: "now",
      scheduledSendAt: null,
      status: "sent",
      updatedAt: createTimestamp(36),
    },
    {
      id: "sms-campaign-welcome-draft",
      name: "Late-night welcome flow",
      templateId: "sms-template-welcome",
      templateName: "Warm welcome follow-up",
      audienceType: "segment",
      segmentIds: ["31"],
      audienceLabel: "New WiFi signups",
      recipientCount: 312,
      deliveredCount: 0,
      clickRate: 0,
      optOutRate: 0,
      sendMode: "scheduled",
      scheduledSendAt: createTimestamp(-72),
      status: "draft",
      updatedAt: createTimestamp(12),
    },
  ];
}

function buildDefaultSmsAutomations() {
  return [
    {
      id: "sms-automation-welcome-main",
      name: "Primary welcome flow",
      description: "Send a short welcome SMS after a guest joins and consents to receive texts.",
      trigger: "welcome",
      triggerLabel: "Welcome automation",
      templateId: "sms-template-welcome",
      templateName: "Warm welcome follow-up",
      type: "sms",
      isActive: true,
      audienceLevel: "organisation",
      audienceBrands: [],
      audienceLocations: [],
      sendDelayMinutes: 15,
      repeatEnabled: false,
      repeatValue: 1,
      repeatUnit: "months",
      recipientCount: 1940,
      deliveredCount: 1904,
      clickRate: 23.4,
      optOutRate: 0.4,
      revenueAttributed: 6280,
      createdAt: createTimestamp(96),
      updatedAt: createTimestamp(5),
    },
    {
      id: "sms-automation-welcome-brand",
      name: "Brand welcome follow-up",
      description: "Send a welcome SMS from a selected brand after a guest signs up.",
      trigger: "welcome",
      triggerLabel: "Welcome automation",
      templateId: "sms-template-welcome",
      templateName: "Warm welcome follow-up",
      type: "sms",
      isActive: true,
      audienceLevel: "brand",
      audienceBrands: ["brand-1"],
      audienceLocations: [],
      sendDelayMinutes: 30,
      repeatEnabled: true,
      repeatValue: 3,
      repeatUnit: "months",
      recipientCount: 1542,
      deliveredCount: 1511,
      clickRate: 12.7,
      optOutRate: 0.2,
      revenueAttributed: 0,
      createdAt: createTimestamp(240),
      updatedAt: createTimestamp(18),
    },
    {
      id: "sms-automation-welcome-location",
      name: "Location welcome reminder",
      description: "Send a short welcome reminder for guests captured at selected locations.",
      trigger: "welcome",
      triggerLabel: "Welcome automation",
      templateId: "sms-template-welcome",
      templateName: "Warm welcome follow-up",
      type: "sms",
      isActive: false,
      audienceLevel: "location",
      audienceBrands: [],
      audienceLocations: ["location-1", "location-2"],
      sendDelayMinutes: 60,
      repeatEnabled: false,
      repeatValue: 1,
      repeatUnit: "months",
      recipientCount: 882,
      deliveredCount: 861,
      clickRate: 19.1,
      optOutRate: 0.3,
      revenueAttributed: 4320,
      createdAt: createTimestamp(420),
      updatedAt: createTimestamp(32),
    },
  ];
}

function getBrowserStorage() {
  if (typeof window === "undefined") {
    return null;
  }

  try {
    return window.localStorage;
  } catch (error) {
    return null;
  }
}

function readCollection(storageKey, buildDefaults) {
  const storage = getBrowserStorage();
  const fallback = buildDefaults();

  if (!storage) {
    return fallback;
  }

  try {
    const raw = storage.getItem(storageKey);
    if (!raw) {
      storage.setItem(storageKey, JSON.stringify(fallback));
      return fallback;
    }

    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) {
      return parsed;
    }
  } catch (error) {
    storage.setItem(storageKey, JSON.stringify(fallback));
  }

  return fallback;
}

function writeCollection(storageKey, records) {
  const storage = getBrowserStorage();
  if (!storage) {
    return;
  }

  storage.setItem(storageKey, JSON.stringify(records));
}

function sortByUpdatedDate(records) {
  return [...records].sort((left, right) => new Date(right.updatedAt) - new Date(left.updatedAt));
}

function normalizeSmsCampaignRecord(campaign) {
  const { purpose, goal, ...rest } = campaign || {};
  return rest;
}

function normalizeSmsAutomationRecord(automation) {
  const {
    purpose,
    goal,
    status,
    quietHours,
    smartSending,
    audienceLabel,
    ...rest
  } = automation || {};

  return {
    ...rest,
    type: "sms",
    trigger: "welcome",
    triggerLabel: "Welcome automation",
    isActive: typeof rest.isActive === "boolean" ? rest.isActive : status === "active",
    audienceLevel: rest.audienceLevel || "organisation",
    audienceBrands: Array.isArray(rest.audienceBrands) ? rest.audienceBrands : [],
    audienceLocations: Array.isArray(rest.audienceLocations) ? rest.audienceLocations : [],
    sendDelayMinutes: Math.max(0, Number(rest.sendDelayMinutes ?? 15)),
    repeatEnabled: Boolean(rest.repeatEnabled),
    repeatValue: Math.max(1, Number(rest.repeatValue ?? 1)),
    repeatUnit: rest.repeatUnit || "months",
    recipientCount: Number(rest.recipientCount || 0),
    deliveredCount: Number(rest.deliveredCount || 0),
    clickRate: Number(rest.clickRate || 0),
    optOutRate: Number(rest.optOutRate || 0),
    revenueAttributed: Number(rest.revenueAttributed || 0),
  };
}

export function getDefaultSmsTemplate() {
  return {
    name: "",
    senderName: "",
    message: "",
    attachmentImageUrl: "",
    attachmentImageName: "",
  };
}

export function readSmsCampaigns() {
  const campaigns = readCollection(SMS_CAMPAIGNS_STORAGE_KEY, buildDefaultSmsCampaigns);
  const normalizedCampaigns = campaigns.map(normalizeSmsCampaignRecord);
  writeCollection(SMS_CAMPAIGNS_STORAGE_KEY, normalizedCampaigns);
  return sortByUpdatedDate(normalizedCampaigns);
}

export function getDefaultSmsAutomation() {
  return {
    name: "",
    description: "",
    type: "sms",
    trigger: "welcome",
    triggerLabel: "Welcome automation",
    templateId: "",
    templateName: "",
    isActive: true,
    audienceLevel: "organisation",
    audienceBrands: [],
    audienceLocations: [],
    sendDelayMinutes: 15,
    repeatEnabled: false,
    repeatValue: 1,
    repeatUnit: "months",
    recipientCount: 0,
    deliveredCount: 0,
    clickRate: 0,
    optOutRate: 0,
    revenueAttributed: 0,
  };
}

export function readSmsAutomations() {
  const automations = readCollection(SMS_AUTOMATIONS_STORAGE_KEY, buildDefaultSmsAutomations);
  const normalizedAutomations = automations.map(normalizeSmsAutomationRecord);
  writeCollection(SMS_AUTOMATIONS_STORAGE_KEY, normalizedAutomations);
  return sortByUpdatedDate(normalizedAutomations);
}

export function getSmsAutomation(automationId) {
  return readSmsAutomations().find((automation) => automation.id === automationId) || null;
}

export function saveSmsAutomation(automation) {
  const now = new Date().toISOString();
  const automations = readSmsAutomations();
  const automationId = automation.id || `sms-automation-${Date.now()}`;
  const existing = automations.find((record) => record.id === automationId);
  const nextRecord = normalizeSmsAutomationRecord({
    ...getDefaultSmsAutomation(),
    ...existing,
    ...automation,
    id: automationId,
    createdAt: existing?.createdAt || now,
    updatedAt: now,
  });
  const remaining = automations.filter((record) => record.id !== automationId);
  const nextAutomations = sortByUpdatedDate([nextRecord, ...remaining]);
  writeCollection(SMS_AUTOMATIONS_STORAGE_KEY, nextAutomations);
  return nextRecord;
}

export function deleteSmsAutomation(automationId) {
  const automations = readSmsAutomations().filter((automation) => automation.id !== automationId);
  writeCollection(SMS_AUTOMATIONS_STORAGE_KEY, automations);
}

export function saveSmsCampaign(campaign) {
  const now = new Date().toISOString();
  const campaigns = readSmsCampaigns();
  const campaignId = campaign.id || `sms-campaign-${Date.now()}`;
  const existing = campaigns.find((record) => record.id === campaignId);
  const nextRecord = normalizeSmsCampaignRecord({
    deliveredCount: 0,
    clickRate: 0,
    optOutRate: 0,
    segmentIds: [],
    sendMode: "now",
    status: "draft",
    ...existing,
    ...campaign,
    id: campaignId,
    updatedAt: now,
  });
  const remaining = campaigns.filter((record) => record.id !== campaignId);
  const nextCampaigns = sortByUpdatedDate([nextRecord, ...remaining]);
  writeCollection(SMS_CAMPAIGNS_STORAGE_KEY, nextCampaigns);
  return nextRecord;
}

export const SMS_OPT_OUT_TEXT = "Text STOP to opt-out.";

export function buildSmsBody(message, options = {}) {
  const { senderName = "", includeOptOut = false } = options;
  const normalizedMessage = String(message || "").trim();
  if (!normalizedMessage) {
    return "";
  }

  const normalizedSenderName = String(senderName || "").trim();
  const formattedMessage = normalizedSenderName
    ? `${normalizedSenderName}: ${normalizedMessage}`
    : normalizedMessage;

  if (!includeOptOut) {
    return formattedMessage;
  }

  return `${formattedMessage}\n\n${SMS_OPT_OUT_TEXT}`;
}

export function interpolateSmsMessage(message, options = {}) {
  return buildSmsBody(message, options);
}

export function calculateSmsMetrics(message, options = {}) {
  const { hasAttachmentImage = false } = options;
  const content = (message || "").replace(/\r\n/g, "\n");
  const characterCount = content.length;
  const unicode = Array.from(content).some((character) => character.charCodeAt(0) > 127);

  if (hasAttachmentImage) {
    const mmsCharacterLimit = 1600;

    return {
      characterCount,
      encoding: unicode ? "Unicode" : "GSM-7",
      transport: "MMS",
      segments: 1,
      credits: 1,
      singleSegmentLimit: mmsCharacterLimit,
      multiSegmentLimit: mmsCharacterLimit,
      segmentCharacterLimit: mmsCharacterLimit,
      remaining: Math.max(mmsCharacterLimit - characterCount, 0),
      overLimit: characterCount > mmsCharacterLimit,
    };
  }

  const singleSegmentLimit = unicode ? 70 : 160;
  const multiSegmentLimit = unicode ? 67 : 153;
  const segments = characterCount === 0
    ? 1
    : characterCount <= singleSegmentLimit
      ? 1
      : Math.ceil(characterCount / multiSegmentLimit);
  const segmentLimit = segments === 1 ? singleSegmentLimit : multiSegmentLimit * segments;

  return {
    characterCount,
    encoding: unicode ? "Unicode" : "GSM-7",
    transport: "SMS",
    segments,
    credits: segments,
    singleSegmentLimit,
    multiSegmentLimit,
    segmentCharacterLimit: segmentLimit,
    remaining: Math.max(segmentLimit - characterCount, 0),
    overLimit: characterCount > segmentLimit,
  };
}

export function formatRelativeTime(timestamp) {
  if (!timestamp) {
    return "Just now";
  }

  const date = new Date(timestamp);
  const diffMs = Date.now() - date.getTime();
  const diffMinutes = Math.round(diffMs / (1000 * 60));

  if (diffMinutes < 1) {
    return "Just now";
  }
  if (diffMinutes < 60) {
    return `${diffMinutes}m ago`;
  }

  const diffHours = Math.round(diffMinutes / 60);
  if (diffHours < 24) {
    return `${diffHours}h ago`;
  }

  const diffDays = Math.round(diffHours / 24);
  if (diffDays < 7) {
    return `${diffDays}d ago`;
  }

  return date.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
  });
}

export function estimateAudienceCount(segments, selectedSegmentIds) {
  if (!selectedSegmentIds?.length) {
    return 0;
  }

  const selectedIds = new Set(selectedSegmentIds.map(String));
  return (segments || []).reduce((total, segment) => {
    if (!selectedIds.has(String(segment.id))) {
      return total;
    }

    return total + Number(segment.contact_count ?? segment.cached_contact_count ?? 0);
  }, 0);
}

export function getAutomationStatusMeta(status) {
  return SMS_AUTOMATION_STATUS_OPTIONS.find((option) => option.value === status) || SMS_AUTOMATION_STATUS_OPTIONS[2];
}

export function getAutomationTriggerMeta(trigger) {
  return SMS_AUTOMATION_TRIGGER_OPTIONS.find((option) => option.value === trigger) || SMS_AUTOMATION_TRIGGER_OPTIONS[0];
}

export function formatSmsDelay(minutes = 0) {
  const totalMinutes = Number(minutes || 0);

  if (totalMinutes <= 0) {
    return "Instant";
  }

  if (totalMinutes % 1440 === 0) {
    const days = totalMinutes / 1440;
    return `${days} day${days === 1 ? "" : "s"}`;
  }

  if (totalMinutes % 60 === 0) {
    const hours = totalMinutes / 60;
    return `${hours} hour${hours === 1 ? "" : "s"}`;
  }

  return `${totalMinutes} min`;
}

export function calculatePercentage(numerator, denominator) {
  if (!denominator) {
    return 0;
  }

  return Number(((Number(numerator || 0) / Number(denominator || 0)) * 100).toFixed(1));
}